import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Stack,
  Popover,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useDateFormat from '../../../../shared/hooks/useDateFormat';
import ConfidenceBreakdown from './ConfidenceBreakdown';
import { confidenceColor } from '../../utils/stagingConstants';

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
    <Typography variant='body2' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='body2' fontWeight='medium'>
      {value || '-'}
    </Typography>
  </Box>
);

/**
 * Displays the candidates list with search bar and candidate accordions.
 */
const StagingCandidatesList = ({
  stagedVoyage,
  candidates,
  loadingCandidates,
  searchQuery,
  setSearchQuery,
  loadingSearch,
  loadingDiff,
  actionLoading,
  onSearch,
  onViewDiff,
  onCreateAsNew,
}) => {
  const { formatDateTime } = useDateFormat();
  const [confidenceAnchorEl, setConfidenceAnchorEl] = useState(null);
  const [confidenceCandidate, setConfidenceCandidate] = useState(null);

  return (
    <>
      {stagedVoyage?.matchedVoyageId && (
        <Alert severity='info' sx={{ mb: 2 }}>
          <Typography variant='body2' fontWeight='bold'>
            Direct Match (PUT Update)
          </Typography>
          <Typography variant='body2'>
            This entry was submitted as a PUT update targeting an existing
            voyage. The match below is a guaranteed 100% confidence direct
            match.
          </Typography>
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          size='small'
          placeholder='Search voyages by number, IMO, ship name, port...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearch()}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={onSearch} disabled={loadingSearch}>
                  {loadingSearch ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SearchIcon />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant='contained'
          color='primary'
          onClick={onCreateAsNew}
          disabled={actionLoading}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Create as New
        </Button>
      </Box>

      {loadingCandidates ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : candidates.length === 0 ? (
        <Alert severity='info'>
          No matching voyages found. You can search manually or create this as a
          new voyage.
        </Alert>
      ) : (
        <>
          <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>
            {candidates.length} match{candidates.length > 1 ? 'es' : ''} found
          </Typography>
          {candidates.map((candidate) => (
            <Accordion key={candidate.voyageId} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  {candidate.confidence > 0 && (
                    <Chip
                      label={`${Math.round(candidate.confidence * 100)}%`}
                      size='small'
                      color={confidenceColor(candidate.confidence)}
                    />
                  )}
                  <Typography variant='body2' fontWeight='bold'>
                    {candidate.voyageNumber || 'No Number'}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {candidate.shipName || candidate.imo} —{' '}
                    {candidate.departurePort} → {candidate.destinationPort}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  <DetailRow
                    label='ETD'
                    value={
                      candidate.actualETD
                        ? formatDateTime(candidate.actualETD)
                        : '-'
                    }
                  />
                  <DetailRow
                    label='ETA'
                    value={
                      candidate.actualETA
                        ? formatDateTime(candidate.actualETA)
                        : '-'
                    }
                  />
                  <DetailRow
                    label='Port Operator'
                    value={candidate.portOperatorName || '-'}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mt: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Button
                      variant='contained'
                      size='small'
                      onClick={() => onViewDiff(candidate)}
                      disabled={loadingDiff}
                    >
                      Compare & Match
                    </Button>
                    {candidate.confidence > 0 && (
                      <Tooltip title='How is this score calculated?'>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            setConfidenceAnchorEl(e.currentTarget);
                            setConfidenceCandidate(candidate);
                          }}
                        >
                          <InfoOutlinedIcon fontSize='small' color='action' />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}

      {/* Confidence Score Breakdown Popover */}
      <Popover
        open={Boolean(confidenceAnchorEl)}
        anchorEl={confidenceAnchorEl}
        onClose={() => {
          setConfidenceAnchorEl(null);
          setConfidenceCandidate(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {confidenceCandidate && stagedVoyage && (
          <ConfidenceBreakdown
            candidate={confidenceCandidate}
            staged={stagedVoyage}
          />
        )}
      </Popover>
    </>
  );
};

export default StagingCandidatesList;
